import { pluralizeWithCount } from "@common/helpers/string";
import { joinWithAnd } from "@helpers/joinWithAnd";
import { APPLY_SCHEDULE_TEMPLATE_BULK_LIMIT } from "@common/schemas/domains/accounting/scheduleApplication.schema";
import { ScheduleLifecycleVerb } from "@common/types/domains/accounting/schedule";
import { ScheduleApplicationParentRow } from "./scheduleApplicationRowHelpers";
import {
    SCHEDULE_DELETE_BODY,
    SCHEDULE_DELETE_REVERSE_LABEL,
} from "../change/scheduleChangeCopy";
import {
    SCHEDULE_MILESTONE_PAUSE_BODY,
    SCHEDULE_MILESTONE_RESUME_BODY,
    SCHEDULE_PAUSE_BODY,
    SCHEDULE_RESUME_SKIP_NOTE,
    SCHEDULE_STOP_BODY,
} from "./scheduleLifecycleCopy";

export type ScheduleBulkLifecycleVerb = Exclude<
    ScheduleLifecycleVerb,
    ScheduleLifecycleVerb.change
>;

export const SCHEDULE_BULK_LIFECYCLE_VERBS: ScheduleBulkLifecycleVerb[] = [
    ScheduleLifecycleVerb.pause,
    ScheduleLifecycleVerb.resume,
    ScheduleLifecycleVerb.cancel,
    ScheduleLifecycleVerb.delete,
];

export const SCHEDULE_CHANGE_POLICY_LABEL = "Change policy";

export const SCHEDULE_BULK_LIFECYCLE_MENU_LABELS: Record<
    ScheduleBulkLifecycleVerb,
    string
> = {
    [ScheduleLifecycleVerb.pause]: "Pause recognition",
    [ScheduleLifecycleVerb.resume]: "Resume recognition",
    [ScheduleLifecycleVerb.cancel]: "Stop schedules",
    [ScheduleLifecycleVerb.delete]: "Remove schedules",
};

export const SCHEDULE_BULK_LIFECYCLE_LIMIT_REASON = `Select up to ${APPLY_SCHEDULE_TEMPLATE_BULK_LIMIT} schedules to update them.`;

export const SCHEDULE_BULK_NONE_ELIGIBLE_REASON =
    "None of the selected schedules can take this action.";

export const SCHEDULE_BULK_LIFECYCLE_SKIP_REASONS: Record<
    ScheduleBulkLifecycleVerb,
    string
> = {
    [ScheduleLifecycleVerb.pause]: "not active",
    [ScheduleLifecycleVerb.resume]: "not paused",
    [ScheduleLifecycleVerb.cancel]: "not active or paused",
    [ScheduleLifecycleVerb.delete]: "can't be removed",
};

export const SCHEDULE_CHANGE_POLICY_SKIP_REASONS = {
    milestone: "milestone schedules keep their policy",
    lineItem: "change the policy from the invoice",
    bill: "change the policy from the bill",
    manual: "manual schedules keep their policy",
    ended: "can no longer be changed",
    noSource: "no payment or invoice to re-apply",
} as const;

export const SCHEDULE_CHANGE_POLICY_DISABLED_REASONS = {
    noneEligible: SCHEDULE_BULK_NONE_ELIGIBLE_REASON,
    loadingPolicies: "Loading policies…",
    policiesFailed: "Failed to load policies",
    noPolicies: "No other policy covers every selected entity.",
} as const;

export interface ScheduleBulkSkippedRow {
    applicationId: string;
    label: string;
    reason: string;
}

const MAX_NAMED_SKIPPED = 3;

function listNames(labels: string[]): string {
    const named = labels.slice(0, MAX_NAMED_SKIPPED);
    const rest = labels.length - named.length;
    return joinWithAnd(rest > 0 ? [...named, `${rest} more`] : named);
}

export function formatScheduleBulkSkippedWarning(options: {
    skipped: ScheduleBulkSkippedRow[];
    selectedCount: number;
}): string | undefined {
    const { skipped, selectedCount } = options;
    if (skipped.length === 0) {
        return undefined;
    }
    const byReason = new Map<string, string[]>();
    for (const row of skipped) {
        byReason.set(row.reason, [
            ...(byReason.get(row.reason) ?? []),
            row.label,
        ]);
    }
    const clauses = [...byReason.entries()].map(
        ([reason, labels]) => `${listNames(labels)} (${reason})`,
    );
    return `${skipped.length} of ${pluralizeWithCount("selected schedule", selectedCount)} will be skipped: ${clauses.join("; ")}.`;
}

export const SCHEDULE_BULK_LIFECYCLE_KEYWORDS: Record<
    ScheduleBulkLifecycleVerb,
    string[]
> = {
    [ScheduleLifecycleVerb.pause]: ["pause", "hold"],
    [ScheduleLifecycleVerb.resume]: ["resume", "continue"],
    [ScheduleLifecycleVerb.cancel]: ["stop", "cancel", "end"],
    [ScheduleLifecycleVerb.delete]: ["remove", "delete"],
};

const PAST_TENSE: Record<ScheduleBulkLifecycleVerb, string> = {
    [ScheduleLifecycleVerb.pause]: "Paused",
    [ScheduleLifecycleVerb.resume]: "Resumed",
    [ScheduleLifecycleVerb.cancel]: "Stopped",
    [ScheduleLifecycleVerb.delete]: "Removed",
};

const INFINITIVE: Record<ScheduleBulkLifecycleVerb, string> = {
    [ScheduleLifecycleVerb.pause]: "pause",
    [ScheduleLifecycleVerb.resume]: "resume",
    [ScheduleLifecycleVerb.cancel]: "stop",
    [ScheduleLifecycleVerb.delete]: "remove",
};

export function formatScheduleBulkLifecycleTitle(options: {
    verb: ScheduleBulkLifecycleVerb;
    count: number;
}): string {
    const schedules = pluralizeWithCount("schedule", options.count);
    switch (options.verb) {
        case ScheduleLifecycleVerb.pause:
            return `Pause recognition on ${schedules}?`;
        case ScheduleLifecycleVerb.resume:
            return `Resume recognition on ${schedules}?`;
        case ScheduleLifecycleVerb.cancel:
            return `Stop ${schedules}?`;
        case ScheduleLifecycleVerb.delete:
            return `Remove ${schedules}?`;
    }
}

type ScheduleBulkBodyRow = Pick<ScheduleApplicationParentRow, "isMilestone">;

function joinBodiesForRows(options: {
    rows: ScheduleBulkBodyRow[];
    ratableBody: string;
    milestoneBody: string;
}): string {
    const { rows } = options;
    const bodies = [
        ...(rows.some((row) => !row.isMilestone) ? [options.ratableBody] : []),
        ...(rows.some((row) => row.isMilestone) ? [options.milestoneBody] : []),
    ];
    if (bodies.length === 1) {
        return bodies[0];
    }
    return bodies
        .map((body) => (body.endsWith(".") ? body : `${body}.`))
        .join(" ");
}

export function formatScheduleBulkLifecycleBody(options: {
    verb: ScheduleBulkLifecycleVerb;
    rows: ScheduleBulkBodyRow[];
}): string {
    switch (options.verb) {
        case ScheduleLifecycleVerb.pause:
            return joinBodiesForRows({
                rows: options.rows,
                ratableBody: SCHEDULE_PAUSE_BODY,
                milestoneBody: SCHEDULE_MILESTONE_PAUSE_BODY,
            });
        case ScheduleLifecycleVerb.resume:
            return joinBodiesForRows({
                rows: options.rows,
                ratableBody: `Select a date to resume the recognition schedules. ${SCHEDULE_RESUME_SKIP_NOTE}`,
                milestoneBody: SCHEDULE_MILESTONE_RESUME_BODY,
            });
        case ScheduleLifecycleVerb.cancel:
            return SCHEDULE_STOP_BODY;
        case ScheduleLifecycleVerb.delete:
            return SCHEDULE_DELETE_BODY;
    }
}

export const SCHEDULE_BULK_LIFECYCLE_CONFIRM_LABELS: Record<
    ScheduleBulkLifecycleVerb,
    string
> = {
    [ScheduleLifecycleVerb.pause]: "Pause schedules",
    [ScheduleLifecycleVerb.resume]: "Resume schedules",
    [ScheduleLifecycleVerb.cancel]: "Stop schedules",
    [ScheduleLifecycleVerb.delete]: SCHEDULE_DELETE_REVERSE_LABEL,
};

export function formatScheduleBulkLifecycleToast(options: {
    verb: ScheduleBulkLifecycleVerb;
    updatedCount: number;
    failedCount: number;
}): string {
    const summary = `${PAST_TENSE[options.verb]} ${pluralizeWithCount("schedule", options.updatedCount)}`;
    return options.failedCount > 0
        ? `${summary}, ${options.failedCount} failed`
        : summary;
}

export function formatScheduleBulkLifecycleFailureToast(options: {
    verb: ScheduleBulkLifecycleVerb;
    firstFailureMessage: string | undefined;
}): string {
    const base = `Failed to ${INFINITIVE[options.verb]} schedules`;
    return options.firstFailureMessage
        ? `${base}. ${options.firstFailureMessage}`
        : base;
}
